<template>
  <div v-if="token" class="subpixel-antialiased">
    <!-- Mobile top bar -->
    <NotificationPermissionTeaser />
    <div class="flex-1 flex flex-col overflow-hidden md:hidden">
      <div class="bg-indigo-600 text-white group w-full p-3 items-center text-xs font-medium">
        <button type="button" class="border-r border-gray-200 pr-4 md:hidden" @click="mobileMenuOpen = true">
          <span class="sr-only">{{ $t('app.openSidebar') }}</span>
          <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    </div>

   <div class="h-full flex">
    <!-- Narrow sidebar -->
    <SideBar v-bind:sidebarNavigation="sidebarNavigation" v-bind:disconnected="SocketDisconnected" v-bind:agent="currentAgent" v-bind:business="currentBusiness" v-on:logout-event="logoutAgent" v-bind:unreadConversationsCount="UnreadConversation" />

    <!-- Mobile menu -->
    <TransitionRoot as="template" :show="mobileMenuOpen">
      <DialogWidget as="div" class="md:hidden" @close="mobileMenuOpen = false">
        <MobileSideBar v-on:close-mobile-menu="closeMobileMenu" v-bind:sidebarNavigation="sidebarNavigation" v-bind:disconnected="SocketDisconnected" v-bind:agent="currentAgent" v-bind:business="currentBusiness" v-on:logout-event="logoutAgent" v-bind:unreadConversationsCount="UnreadConversation" />
      </DialogWidget>
    </TransitionRoot>

    <!-- Content area -->
      <!-- Main content -->
      <div class="flex-1 flex items-stretch overflow-y-auto h-screen scrollbar-thin scrollbar scrollbar-thumb-indigo-300 scrollbar-track-indigo-50">
        <component :is="ViewComponent" v-if="currentAgent"></component>
      </div>
    </div>
  </div>
  <div v-if="!token && prefetchedEnded" class="subpixel-antialiased">
    <component :is="AuthenticationComponent" v-on:authentication-event="setupDashboardForAgent" />
  </div>
  <div v-if="!token && !prefetchedEnded" class="flex loading-container subpixel-antialiased">
    <Loading :text="$t('app.connecting')" />
  </div>
</template>

<script>
import SideBar from './components/page/SideBar.vue'
import MobileSideBar from './components/page/MobileSideBar.vue'
import ConversationsPage from './components/ConversationsPage.vue'
import NotFound from './components/NotFound.vue'
import Settings from './components/SettingsPage.vue'
import Statistics from './components/StatisticsPage.vue'
import Agents from './components/AgentsPage'
import Customers from './components/CustomersPage';
import Login from './components/LoginPage';
import Join from './components/JoinPage';
import Loading from './components/page/LoadingWidget.vue'
import VLink from './components/page/VLink.vue'
import AgentAvatar from './components/agents/AgentAvatar.vue'
import ChatIcon from './components/page/icons/ChatIcon.vue'
import CustomersIcon from './components/page/icons/CustomersIcon.vue'
import AgentsIcon from './components/page/icons/AgentsIcon.vue'
import SettingsIcon from './components/page/icons/SettingsIcon.vue'
import StatisticsIcon from './components/page/icons/StatisticsIcon.vue'
import NotificationPermissionTeaser from "@/components/page/partials/NotificationPermissionTeaser";

import facebookFanpageModel from '@/models/facebook-fanpage';
import vonageModel from '@/models/vonage-ingration';
import groupModel from '@/models/group'
import agentModel from '@/models/agent'
import businessModel from '@/models/business'
import automationModel from '@/models/automation'
import cannedMessageModel from '@/models/cannedMessage'
import dashboardSettingsModel from '@/models/dashboardSettings'
import whatsappMessageTemplatesModel from '@/models/whatsappMessageTemplates'
import { useConversationsStore } from '@/stores/conversations'
import { useCustomerStore } from '@/stores/customers'
import { useAgentStore } from '@/stores/agents'
import { useFanpagesStore } from '@/stores/fanpages';
import { useVonageStore } from '@/stores/vonageIntegrations';
import { useMessageAutomationStore } from '@/stores/automation';
import { useGroupStore } from '@/stores/groups';
import { useCannedMessagesStore } from '@/stores/cannedMessages';
import { useAgentIsTypingStore } from '@/stores/agentIsTyping';
import { useReasonForResolvingStore } from '@/stores/reasonForResolving';
import { useWhatsappMessagesStore } from '@/stores/whatsappMessageTemplates';
import reasonModel from "@/models/reason";

import { io } from 'socket.io-client';
import smartlookClient from 'smartlook-client'
import * as Sentry from '@sentry/vue';

import { ref } from 'vue'
import {
  Dialog as DialogWidget,
  DialogOverlay,
  TransitionRoot,
} from '@headlessui/vue'
import {
  MenuAlt2Icon,
} from '@heroicons/vue/outline'
import { SearchIcon } from '@heroicons/vue/solid'

const sidebarNavigation = [
  { name: 'sidebar.chat', href: '/conversations', icon: ChatIcon, isConversations: true },
  { name: 'sidebar.agents', href: '/agents', icon: AgentsIcon },
  { name: 'sidebar.customers', href: '/customers', icon: CustomersIcon },
  { name: 'sidebar.statistics', href: '/statistics', icon: StatisticsIcon },
  { name: 'sidebar.settings', href: '/settings/facebook', icon: SettingsIcon },
]

const routes = {
  '/': ConversationsPage,
  '/conversations': ConversationsPage,
  '/agents': Agents,
  '/customers': Customers,
  '/settings': Settings,
  '/statistics': Statistics,
}
const authenticationRoutes = {
  '/join': Join,
}

export default {
  name: 'App',
  components: {
    NotificationPermissionTeaser,
    Login,
    SideBar,
    MobileSideBar,
    Loading,
    VLink,
    AgentAvatar,
    //
    DialogWidget,
    DialogOverlay,
    TransitionRoot,
    MenuAlt2Icon,
    SearchIcon,
  },
  setup() {
    const mobileMenuOpen = ref(false)
    let conversationsStore = useConversationsStore();
    let customerStore = useCustomerStore();
    let agentStore = useAgentStore();
    let fanpagesStore = useFanpagesStore();
    let vonageStore = useVonageStore();
    let automationStore = useMessageAutomationStore();
    let groupStore = useGroupStore();
    let cannedMessagesStore = useCannedMessagesStore();
    let agentIsTypingStore = useAgentIsTypingStore();
    let reasonStore = useReasonForResolvingStore();
    let whatsappMessages = useWhatsappMessagesStore();
    return {
      conversationsStore,
      customerStore,
      agentStore,
      sidebarNavigation,
      mobileMenuOpen,
      fanpagesStore,
      vonageStore,
      automationStore,
      groupStore,
      cannedMessagesStore,
      agentIsTypingStore,
      reasonStore,
      whatsappMessages,
    };
  },
  created: function () {
    let self = this;
    enableRouting();
    this.tryPrefetchingToken();
    this.refreshTokenInInterval();
    if (process.env.VUE_APP_USE_SMART_LOOK === 'true') {
      smartlookClient.init('eed5900c262a073a6b3fc68bcfaae407db629e88', {region: 'eu'});
    }

    function enableRouting() {
      window.onpopstate = function() {
        self.currentRoute = window.location.pathname;
      };
    }
    document.addEventListener("visibilitychange", () => {
      this.pageHidden = document.hidden;
    });
  },
  methods: {
    async refreshTokenInInterval() {
      console.log('refreshTokenInInterval');
      let interval = setInterval(async () => {
        console.log('refreshTokenInInterval interval');
        let token = await this.getTokenForAgent();
        if (token) {
          this.token = token;
        } else {
          clearInterval(interval);
          location.assign('https://dashboard.chatpilot.io/');
        }
      }, 1200000);
    },
    async watchForSocketIoConnectionError() {
      this.sockets.message.on('connect_error', async () => {
        console.log('socket connect_error');
        let token = await this.getTokenForAgent();
        if (token) {
          this.token = token;
          this.connectToSockets(true);
        } else {
          this.sockets.socketAuthenticationError = true;
        }
      });
      this.sockets.business.on('connect_error', async () => {
        console.log('business socket connect_error');
        let token = await this.getTokenForAgent();
        if (token) {
          this.token = token;
          this.connectToSockets(true);
        } else {
          this.sockets.socketAuthenticationError = true;
        }
      });
    },
    watchBusinessSocketConnection() {
      this.sockets.business.on('connect', () => {
        console.log('socket connect');
        this.sockets.businessSocketReady = true;
        if (this.sockets.businessBackendDisconnected) {
          this.sockets.business.emit('join_agents', this.token);
          setTimeout(() => {
            console.log('socket connect setTimeout');
            this.pushAwaitingBusinessEvents();
          }, 5000);
          this.sockets.businessBackendDisconnected = false;
        }
      });
      this.sockets.business.on('disconnect', () => {
        console.log('business socket disconnect');
        this.sockets.businessBackendDisconnected = true;
        setTimeout(() => {
          this.setSocketHardDisconnectIfPersistent();
        }, 15000);
      });
      this.sockets.business.on('agentIsTyping', (data) => {
        console.log('business socket agentIsTyping');
        let id = `${data.agent_id}/${data.conversation_id}`;
        let entry = {'agentId': data.agent_id, 'conversationId': data.conversation_id, 'time': new Date(), 'id': id};
        this.agentIsTypingStore.replaceOrAdd(entry);
      });
    },
    watchMessageSocketEvents() {
      this.sockets.message.on('connect', () => {
        console.log('socket connect');
        this.sockets.messageSocketReady = true;
        if (this.sockets.messageBackendDisconnected) {
          this.sockets.message.emit('join_agents', this.token);
          setTimeout(() => {
            console.log('socket connect setTimeout');
            this.pushAwaitingMessageEvents();
          }, 5000);
          this.sockets.messageBackendDisconnected = false;
        }
      });
      this.sockets.message.on('disconnect', () => {
        console.log('socket disconnect');
        this.sockets.messageBackendDisconnected  = true;
        setTimeout(() => {
          console.log('socket disconnect setTimeout');
          this.setSocketHardDisconnectIfPersistent();
        }, 15000);
      });
      this.sockets.message.on('removeConversation', (conversationId) => {
        console.log('socket removeConversation');
        this.conversationsStore.removeById(conversationId);
      });
      this.sockets.message.on('removeCustomer', (customerId) => {
        console.log('socket removeCustomer');
        this.customerStore.removeById(customerId);
      });
      this.sockets.message.on('propagateCustomer', (response) => {
        console.log('socket propagateCustomer');
        this.customerStore.replaceOrAdd(response.data);
      });
      this.sockets.message.on('propagateConversation', (conversation, customer, latest_customer_message, latest_message) => {
        console.log('socket propagateConversation');
        conversation.customer = customer.data;
        conversation.latest_message = latest_message;
        conversation.latest_customer_message = latest_customer_message;
        let action = this.conversationsStore.replaceOrAdd(conversation);
        if (action === this.conversationsStore.addActionLabel) {
          this.customerStore.replaceOrAdd(customer.data);
          if (conversation.status === 'created') {
            if (this.agentDashboardSettings.attributes['notifications-level'] !== 'disabled') {
              let text = conversation.latest_customer_message.text;
              let author = conversation.customer.attributes.name;
              let message = this.$t('conversation.newConversation', {author: author});
              this.$notification.show(message, {
                body: text,
              }, {
                onclick: function () {
                  window.location.href = `/conversations/${conversation.id}`;
                },
              });
            }
          }
        }
      });
      this.sockets.message.on('authenticationFailure', async (originalEvent) => {
        console.log('socket authenticationFailure');
        let token = await this.getTokenForAgent();
        if (token) {
          this.token = token;
          this.messageEmit(originalEvent.name, this.token, ...originalEvent.args);
        } else {
          this.events.failedMessageEvents.push(originalEvent);
          this.token = '';
        }
      });
    },
    async tryPrefetchingToken() {
      console.log('tryPrefetchingToken');
      let token = await this.getTokenForAgent();
      if (token) {
        this.token = token;
        this.connectToSockets();
      }
      this.prefetchedEnded = true;
    },
    connectToSockets(force) {
      console.log('connectToSockets');
      if (!this.sockets.message || force) {
        this.sockets.message = io(process.env.VUE_APP_HYD_MESSAGE_WS_ENDPOINT, {transports: ["websocket"], auth: this.token});
      }
      if (!this.sockets.business || force) {
        this.sockets.business = io(process.env.VUE_APP_HYD_BUSINESS_WS_ENDPOINT, {transports: ["websocket"], auth: this.token});
      }
      this.watchForSocketIoConnectionError();
      this.watchBusinessSocketConnection();
      this.watchMessageSocketEvents();
    },
    async setupDashboardForAgent() {
      console.log('setupDashboardForAgent');
      let token = await this.getTokenForAgent();
      if (token) {
        console.log('setupDashboardForAgent has token');
        this.token = token;
        await this.connectToSockets();
        this.setupDashboard();
        this.retryFailedEvents();
      }
    },
    async getTokenForAgent() {
      let response;
      try {
        response = await fetch("/authentication/token/");
      } catch(error) {
        Sentry.captureMessage(error.toLocaleString());
      }
      if (response) {
        if (response.status === 200) {
          let data = await response.json();
          return data.token;
        }
      }
    },
    async setupDashboard() {
      console.log('setupDashboard');
      if (!this.initialized) {
        console.log('setupDashboard initializing');
        this.messageEmit('join_agents', this.token);
        this.businessEmit('join_agents', this.token);
        await this.getAgents();
        await this.getAutomations();
        await this.getExistingReasons();
        await this.getCannedMessages();
        await this.getWhatsappMessageTemplates();
        await groupModel.getExistingGroups(this.axios, this.groupStore, this.token);
        await facebookFanpageModel.getExistingFanpages(this.axios, this.fanpagesStore, this.token);
        await vonageModel.getExistingIntegrations(this.axios, this.vonageStore, this.token);
        this.currentAgent = await this.getCurrentAgent();
        this.agentDashboardSettings = await this.getDashboardSettings();
        if (process.env.VUE_APP_USE_SMART_LOOK === 'true') {
          smartlookClient.identify(this.currentAgent.id, {
            name: this.currentAgent.attributes.name,
            email: this.currentAgent.attributes.email,
          });
        }
        if (process.env.VUE_APP_SENTRY_DSN) {
          Sentry.setUser({ email: this.currentAgent.attributes.email, name: this.currentAgent.attributes.name });
        }
        this.currentBusiness = businessModel.fetchCurrentBusiness(this.axios, this.$root.token);
        this.sockets.business.on('authenticationFailure', async (originalEvent) => {
          console.log('setupDashboard authenticationFailure');
          let token = await this.getTokenForAgent();
          if (token) {
            this.token = token;
            this.businessEmit(originalEvent.name, this.token, ...originalEvent.args);
          } else {
            this.events.failedBusinessEvents.push(originalEvent);
            this.token = '';
          }
        });
        this.initialized = true;
      }
    },
    async getAgents() {
      console.log('getAgents');
      let agents = await agentModel.fetchAllAgents(this.axios, this.$root.token);
      agents.data.data.forEach(agent => {
        this.agentStore.replaceOrAdd(agent);
      });
    },
    async getCannedMessages() {
      console.log('getCannedMessages');
      let messages = await cannedMessageModel.fetchAllMessages(this.axios, this.$root.token);
      messages.data.data.forEach(message => {
        this.cannedMessagesStore.replaceOrAdd(message);
      });
    },
    async getWhatsappMessageTemplates() {
      console.log('getWhatsappMessageTemplates');
      let templates = await whatsappMessageTemplatesModel.fetchAllTemplates(this.axios, this.$root.token);
      templates.data.data.forEach(template => {
        this.whatsappMessages.replaceOrAdd(template);
      });
    },
    async getAutomations() {
      console.log('getAutomations');
      let automations = await automationModel.fetchAllAutomations(this.axios, this.$root.token);
      automations.data.data.forEach(automation => {
        this.automationStore.replaceOrAdd(automation);
      });
    },
    async getExistingReasons() {
      console.log('getExistingReasons');
      return await reasonModel.getExistingReasons(this.axios, this.reasonStore, this.$root.token);
    },
    async getDashboardSettings() {
      console.log('getDashboardSettings');
      let response = await dashboardSettingsModel.fetchSettings(this.axios, this.$root.token);
      return response.data.data;
    },
    async getCurrentAgent() {
      console.log('getCurrentAgent');
      let response = await agentModel.fetchCurrentAgent(this.axios, this.$root.token);
      return response.data.data;
    },
    retryFailedEvents() {
      console.log('retryFailedEvents');
      this.events.failedMessageEvents.forEach(event => {
        this.messageEmit(event.name, this.token, ...event.args);
      });
      this.events.failedMessageEvents = [];
      this.events.failedBusinessEvents.forEach(event => {
        this.businessEmit(event.name, this.token, ...event.args);
      });
      this.events.failedBusinessEvents = [];
    },
    pushAwaitingMessageEvents() {
      console.log('pushAwaitingMessageEvents');
      this.events.awaitingMessageEvents.forEach(event => {
        this.messageEmit(event.name, this.token, ...event.args);
      });
      this.events.awaitingMessageEvents = [];
    },
    pushAwaitingBusinessEvents() {
      console.log('pushAwaitingBusinessEvents');
      this.events.awaitingBusinessEvents.forEach(event => {
        this.businessEmit(event.name, this.token, ...event.args);
      });
      this.events.awaitingBusinessEvents = [];
    },
    async logoutAgent() {
      console.log('logoutAgent');
      let response = await fetch("/authentication/logout/");
      if (response.status === 200) {
        await window.location.assign('/');
      }
    },
    async messageEmit(eventName, token, ...args) {
      console.log('- messageEmit', eventName);
      if (this.sockets.messageBackendDisconnected) {
        this.events.awaitingMessageEvents.push({
          'name': eventName,
          'args': args,
        });
      } else {
        await this.sockets.message.emit(eventName, token, ...args);
      }
    },
    async businessEmit(eventName, token, ...args) {
      console.log('- businessEmit', eventName);
      if (this.sockets.businessBackendDisconnected) {
        this.events.awaitingBusinessEvents.push({
          'name': eventName,
          'args': args,
        });
      } else {
        await this.sockets.business.emit(eventName, token, ...args);
      }
    },
    setSocketHardDisconnectIfPersistent() {
      console.log('setSocketHardDisconnectIfPersistent');
      if (this.SocketDisconnected) {
        this.sockets.socketHardDisconnect = true;
        let interval = setInterval(() => {
          console.log('setSocketHardDisconnectIfPersistent interval');
          if (!this.SocketDisconnected) {
            this.sockets.socketHardDisconnect = false;
            clearInterval(interval);
          }
        }, 2000);
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
    },
    runTitleAnimation() {
      if (this.titleAnimationInterval) {
        clearInterval(this.titleAnimationInterval);
      }
      this.titleAnimationInterval = setInterval(() => {
        if (this.titlePhase) {
          document.title = `(${this.UnreadConversation}) ChatPilot`;
          this.titlePhase = false;
        } else {
          document.title = 'ChatPilot';
          this.titlePhase = true;
        }
      }, 1000);
    }
  },
  computed: {
    ViewComponent () {
      let result = routes[this.Route];
      if (!result) {
        return NotFound;
      }
      if (typeof result === 'function') {
        return result(this.RouteArgs);
      } else {
        return result;
      }
    },
    AuthenticationComponent () {
      let result = authenticationRoutes[this.Route];
      if (!result) {
        return Login;
      }
      if (typeof result === 'function') {
        return result(this.RouteArgs);
      } else {
        return result;
      }
    },
    Route() {
      let routeArgs = this.currentRoute.split('/');
      return `/${routeArgs[1]}`;
    },
    RouteArgs() {
      return this.currentRoute.split('/').slice(2);
    },
    SocketsReady() {
      return this.sockets.messageSocketReady && this.sockets.businessSocketReady;
    },
    SocketDisconnected() {
      return this.sockets.messageBackendDisconnected || this.sockets.businessBackendDisconnected;
    },
    UnreadConversation() {
      if (this.currentAgent) {
        return this.conversationsStore.unreadConversationsCount(this.currentAgent.id);
      } else {
        return 0;
      }
    },
    Beta() {
      return localStorage.beta;
    }
  },
  watch: {
    SocketsReady: function(newStatus, oldStatus) {
      if (newStatus === true && oldStatus === false) {
        if (this.token) {
          this.setupDashboard();
        } else {
          this.setupDashboardForAgent();
        }
      }
    },
    UnreadConversation(newCount) {
      if (newCount > 0) {
        this.runTitleAnimation();
      } else {
        if (this.titleAnimationInterval) {
          clearInterval(this.titleAnimationInterval);
        }
        document.title = 'ChatPilot';
      }
    },
    agentDashboardSettings(settings) {
      let validLocale = ['en', 'pl', 'es'];
      if (settings && settings.attributes.language && validLocale.indexOf(settings.attributes.language) !== -1) {
        this.$i18n.locale = settings.attributes.language;
      }
    }
  },
  data() {
    return {
      initialized: false,
      currentRoute: window.location.pathname,
      token: undefined,
      currentBusiness: undefined,
      currentAgent: undefined,
      agentDashboardSettings: undefined,
      prefetchedEnded: false,
      titleAnimationInterval: undefined,
      titlePhase: false,
      pageHidden: false,
      sockets: {
        business: undefined,
        message: undefined,
        messageSocketReady: false,
        businessSocketReady: false,
        messageBackendDisconnected: false,
        businessBackendDisconnected: false,
        socketHardDisconnect: false,
        socketAuthenticationError: false,
      },
      events: {
        failedMessageEvents: [],
        failedBusinessEvents: [],
        awaitingMessageEvents: [],
        awaitingBusinessEvents: [],
      }
    }
  },
}
</script>
